/**
 * @file Component wrapping FlatPickr for date range picking.
 */
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import * as moment from 'moment';
// @ts-expect-error Not yet typed.
import FlatPickr from 'vue-flatpickr-component';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Reset } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { datetime } from '@codegrade/utils';
import CalendarSettings from '@/components/CalendarSettings';
import { Icon } from '@/components/Icon';
import { CalendarConfig } from '@/models/calendar_config';
import { getUniqueId, ifJustOrEmpty } from '@/utils';
import { formatMaxDate, formatMinDate } from './common';
import 'flatpickr/dist/flatpickr.css';
import 'flatpickr/dist/plugins/confirmDate/confirmDate.css';
import './DatePicker.less';
export default tsx.componentFactoryOf().create({
    name: 'date-range-picker',
    props: {
        value: p.ofType().required,
        config: p.ofType().default(() => ({})),
        noYear: p(Boolean).default(false),
        disabled: p(Boolean).default(false),
        placeholder: p(String).optional,
        popover: p(String).default(''),
        id: p(String).optional,
        reset: p.ofType().optional,
        calendarConfig: p.ofType().default(() => CalendarConfig),
    },
    /** Load the calendar configuration. */
    created() {
        this.calendarConfig.load();
    },
    computed: {
        /** @returns A unique id for the DatePicker. */
        uniqueId() {
            return getUniqueId();
        },
        /** @returns A default unique id for the component or the one passed via prop. */
        computedId() {
            if (this.id == null) {
                return `date-range-picker-${this.uniqueId}`;
            }
            return this.id;
        },
        /** @returns The first day of the week. */
        firstDayOfWeek() {
            return this.calendarConfig.get().firstDayOfWeek;
        },
        /** @returns The button to open the calendar configuration modal. */
        calendarConfigNode() {
            return (<b-input-group-append>
                    <CalendarSettings calendarConfig={this.calendarConfig}/>
                </b-input-group-append>);
        },
    },
    methods: {
        // The nodes are not within computed properties to force re-renders on changed values.
        /**
         * Computes the slots to be passed to `b-input-group` component if present,
         * otherwise returns an empty object.
         * This is necessary because if the key is present even with no value an empty node
         * is rendered in the prepend, causing styling issues.
         *
         * @returns An object containing the slots to be passed to the subcomponent.
         */
        getBodyScopedSlots() {
            const prepend = this.$scopedSlots.prepend;
            if (prepend != null) {
                return { prepend };
            }
            else {
                return {};
            }
        },
        /**
         * Computes the Reset button node according to the prop passed to the component.
         *
         * @param reset The non-null version of the reset prop.
         * @returns The node rendering the Reset button or an empty node.
         */
        renderResetButton(reset) {
            return (<b-input-group-append class="reset-button-wrapper" v-b-popover_top_hover_focus={reset.popoverText}>
                    <b-button id={`${this.computedId}-reset`} onClick={() => this.makeEventEmitter(true)([])} disabled={this.disabled || reset.disabled} variant="warning">
                        <Icon icon={Reset}/>
                        <span class="sr-only">Reset value</span>
                    </b-button>
                </b-input-group-append>);
        },
        /**
         * Computes the node containing the FlatPickr component.
         *
         * @returns The node rendering the FlatPickr component.
         */
        renderBodyNode() {
            return (<FlatPickr id={this.computedId} class={['form-control date-range-picker', { 'no-year': this.noYear }]} value={this.getInternalValue().extract()} config={this.getInternalConfig()} disabled={this.disabled} placeholder={this.placeholder}/>);
        },
        /**
         * Formats the moment array passed as a prop to a string array to be passed to FlatPickr.
         *
         * @returns A Maybe containing the correctly formatted dates from the `value` prop.
         */
        getInternalValue() {
            return this.value
                .filter(value => value.every(singleDate => singleDate.isValid()))
                .map(value => value.map(singleDate => datetime.formatDate(singleDate)));
        },
        /**
         * Integrates the default FlatPickr configuration with possible overrides passed to the
         * component in the `config` prop. For this component the FlatPickr mode is forcibly
         * set to range.
         *
         * @returns The configuration object to be passed to FlatPickr.
         */
        getInternalConfig() {
            const { minDate, maxDate, ...config } = this.config;
            return {
                enableTime: true,
                plugins: [
                    confirmDatePlugin({
                        showAlways: true,
                        confirmIcon: '',
                        confirmText: 'Close',
                    }),
                ],
                minuteIncrement: 1,
                time_24hr: true,
                locale: {
                    firstDayOfWeek: this.firstDayOfWeek,
                },
                onChange: this.makeEventEmitter(),
                defaultHour: 23,
                defaultMinute: 59,
                static: this.noYear,
                ...config,
                minDate: formatMinDate(minDate),
                maxDate: formatMaxDate(maxDate),
                // forces this component to behave in range mode. Other modes are encoded in other components.
                mode: 'range',
            };
        },
        /**
         * Returns an event emitter callback.
         *
         * @param emitOnEmpty Whether the callback that is returned will emit with an empty value.
         * @returns The callback to be executed when it is necessary to emit the event.
         */
        makeEventEmitter(emitOnEmpty = false) {
            return (selectedDates) => {
                NonEmptyList.fromArray(selectedDates)
                    .map(arr => arr.map(el => moment.utc(el).local()))
                    .filter(arr => arr.every(el => el.isValid()))
                    .caseOf({
                    Nothing: () => {
                        if (emitOnEmpty) {
                            tsx.emitOn(this, 'onInput', Maybe.empty());
                        }
                    },
                    Just: selectedMoments => {
                        if (selectedMoments.length !== 2) {
                            return;
                        }
                        tsx.emitOn(this, 'onInput', Maybe.of({ start: selectedMoments[0], end: selectedMoments[1] }));
                    },
                });
            };
        },
    },
    /**
     * Renders the main body of the component.
     *
     * @returns The node containing the component body.
     */
    render() {
        return (<b-input-group class="date-range-picker flex-nowrap" v-b-popover_top_hover_focus={this.popover} scopedSlots={this.getBodyScopedSlots()}>
                {this.renderBodyNode()}
                {ifJustOrEmpty(Maybe.fromNullable(this.reset), this.renderResetButton)}
                {this.calendarConfigNode}
            </b-input-group>);
    },
});
